import { Box, Button, Typography } from "@material-ui/core"
import { useState } from "react"
import { nodePalette } from "../../../../../theme"
import simpleCircleGraphics from "../../../../graphics/SimpleCircleGraphics"
import ManaParticle from "../../../ManaParticle"
import InvertableManaNode, { InvertableManaNodeOptions } from "../../InvertableManaNode"
import NodeCategories from "../../NodeCategories"

export type KeyGateNodeOptions = { key?: string } & InvertableManaNodeOptions

export default class KeyGateNode extends InvertableManaNode {

	static nodeName = "Keyboard gate node"
	static nodeDesc = "Allow or block flow of particles using keyboard input"
	static nodeIcon = '⌨'
	static category = NodeCategories.Logistics
	static nodeType = 'KeyGateNode'

	key = undefined as (string | undefined)
	gateOpen

	constructor({ key = undefined, ...rest }: KeyGateNodeOptions) {
		super(rest)

		this.key = key

		this.gateOpen = this.inverted

		this.handleKeyDown = this.handleKeyDown.bind(this)
		this.handleKeyUp = this.handleKeyUp.bind(this)

		this.symbol = this.getSymbol()

		document.addEventListener('keydown', this.handleKeyDown)
		document.addEventListener('keyup', this.handleKeyUp)
	}

	getTooltip() {

		const BindKeyTooltip = () => {

			const [boundKey, setBoundKey] = useState((this.key || 'NONE').toUpperCase())
			const [awaitingInput, setAwaitingInput] = useState(false)

			return (
				<>
					{super.getTooltip()}
					<Box display="flex" gridGap="8px" justifyContent="space-between" alignItems="center">
						<Typography variant="subtitle2">Bound to key: {boundKey}</Typography>
						<Button
							variant="contained"
							disableElevation
							color={awaitingInput ? "primary" : undefined}
							onClick={() => setAwaitingInput(true)}
							onKeyDown={(event) => {
								const key = event.key
								if (key !== 'Escape') {
									setBoundKey(key.toUpperCase())
									this.setKey(key.length > 1 ? key : key.toLowerCase())
								} else {
									setBoundKey('')
									this.setKey(undefined)
								}
								setAwaitingInput(false)
								event.stopPropagation()
								event.preventDefault()
							}}
							onBlur={() => setAwaitingInput(false)}
							style={{ pointerEvents: awaitingInput ? 'none' : undefined }}
						>
							{awaitingInput ? 'AWAITING KEY INPUT...' : 'CLICK TO BIND'}
						</Button>
					</Box>
				</>
			)
		}

		return (
			<BindKeyTooltip />
		)
	}

	getSymbol() {
		const keyChar = (this.key || KeyGateNode.nodeIcon).toUpperCase()

		if (this.inverted) return '❗' + keyChar
		return keyChar
	}

	onScroll(dir: number) { }

	setKey(key: (string | undefined)) {
		this.key = key
		this.symbol = this.getSymbol()
		this.gateOpen = this.inverted
		this.reloadSymbol()
	}

	setInverted(inverted: boolean) {
		super.setInverted(inverted)
		this.gateOpen = this.inverted
	}

	allowEntry(particle: ManaParticle) {
		return this.gateOpen
	}

	handleKeyDown(event: KeyboardEvent) {
		if (!this.world?.caster.isDead) {
			if (event.key === this.key) {
				this.gateOpen = !this.inverted
			}
		}
	}
	handleKeyUp(event: KeyboardEvent) {
		if (!this.world?.caster.isDead) {
			if (event.key === this.key) {
				this.gateOpen = this.inverted
			}
		}
	}

	createGraphics() {

		const graphics = simpleCircleGraphics({
			obj: this,
			x: this.position.x,
			y: this.position.y,
			size: this.size,
			symbol: this.symbol,
			color: this.color,
			border: () => {
				return this.gateOpen ? (
					nodePalette.selection
				) : (
					this.particles.length ? nodePalette.active : nodePalette.border
				)
			},
			evented: true
		})

		return graphics
	}

	dispose() {
		super.dispose()
		document.removeEventListener('keydown', this.handleKeyDown)
		document.removeEventListener('keyup', this.handleKeyUp)
	}

	save() {
		return {
			...super.save(),

			key: this.key,
			inverted: this.inverted
		}
	}

}